import axios from 'axios';

const BLS_API_BASE_URL = 'https://api.bls.gov/publicAPI/v2/timeseries/data';
const BLS_POPULAR_SERIES_URL = 'https://api.bls.gov/publicAPI/v2/timeseries/popular';

// BLS API Rate Limits: 25 requests per day, 500 requests per day with registration
const BLS_API_KEY = import.meta.env.VITE_BLS_API_KEY || 'c3fac023b9da4e428672642682a1dd95';

class BlsApiService {
  constructor() {
    this.baseURL = BLS_API_BASE_URL;
    this.popularSeriesURL = BLS_POPULAR_SERIES_URL;
    this.apiKey = BLS_API_KEY;
  }

  /**
   * Get popular BLS series (no API key required)
   */
  async getPopularSeries() {
    try {
      const response = await axios.get(this.popularSeriesURL);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching popular BLS series:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get employment data for specific occupation series
   * @param {string} seriesId - BLS series ID (e.g., 'CES0000000001' for total nonfarm employment)
   * @param {number} startYear - Start year for data
   * @param {number} endYear - End year for data
   */
  async getEmploymentData(seriesId, startYear = 2020, endYear = 2024) {
    try {
      const requestBody = {
        seriesid: [seriesId],
        startyear: startYear.toString(),
        endyear: endYear.toString(),
        ...(this.apiKey && { registrationkey: this.apiKey })
      };

      const response = await axios.post(this.baseURL, requestBody, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching employment data for series ${seriesId}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get wage data for specific occupation
   * @param {string} occupationCode - SOC occupation code
   * @param {string} stateCode - State code (optional, defaults to national)
   */
  async getWageData(occupationCode, stateCode = null) {
    try {
      // For OEWS wage data, we need to construct the appropriate series ID
      // Format: OEWS + state code + occupation code + data type
      let seriesId = 'OEWS';
      
      if (stateCode) {
        seriesId += stateCode;
      } else {
        seriesId += '000000'; // National data
      }
      
      seriesId += occupationCode;
      seriesId += '000000'; // All industries

      const requestBody = {
        seriesid: [seriesId],
        startyear: '2020',
        endyear: '2024',
        ...(this.apiKey && { registrationkey: this.apiKey })
      };

      const response = await axios.post(this.baseURL, requestBody, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching wage data for occupation ${occupationCode}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get employment projections data
   * @param {string} occupationCode - SOC occupation code
   */
  async getEmploymentProjections(occupationCode) {
    try {
      // Employment projections use different series IDs
      const seriesId = `EP${occupationCode}000000`; // Employment projections format
      
      const requestBody = {
        seriesid: [seriesId],
        startyear: '2020',
        endyear: '2030',
        ...(this.apiKey && { registrationkey: this.apiKey })
      };

      const response = await axios.post(this.baseURL, requestBody, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching employment projections for occupation ${occupationCode}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Search for occupation-related series
   * @param {string} keyword - Search keyword
   */
  async searchOccupationSeries(keyword) {
    try {
      // This would require the BLS search API endpoint
      // For now, we'll use a predefined mapping of common occupations
      const occupationMappings = {
        'software developer': 'CES5051910001',
        'data scientist': 'CES5051910002',
        'nurse': 'CES6211110001',
        'teacher': 'CES2520100001',
        'engineer': 'CES1720100001'
      };

      const seriesId = occupationMappings[keyword.toLowerCase()];
      if (!seriesId) {
        return { success: false, error: 'Occupation not found in predefined mappings' };
      }

      return await this.getEmploymentData(seriesId);
    } catch (error) {
      console.error(`Error searching for occupation series: ${keyword}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get comprehensive occupation data (employment + wages + projections)
   * @param {string} occupationCode - SOC occupation code
   * @param {string} stateCode - State code (optional)
   */
  async getComprehensiveOccupationData(occupationCode, stateCode = null) {
    try {
      const [employmentData, wageData, projectionsData] = await Promise.all([
        this.getEmploymentData(occupationCode),
        this.getWageData(occupationCode, stateCode),
        this.getEmploymentProjections(occupationCode)
      ]);

      return {
        success: true,
        data: {
          employment: employmentData,
          wages: wageData,
          projections: projectionsData,
          occupationCode,
          stateCode,
          lastUpdated: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error(`Error fetching comprehensive data for occupation ${occupationCode}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update occupation data in database using BLS data
   * @param {string} occupationCode - SOC occupation code
   * @param {string} stateCode - State code (optional)
   */
  async updateOccupationFromBls(occupationCode, stateCode = null) {
    try {
      const blsData = await this.getComprehensiveOccupationData(occupationCode, stateCode);
      
      if (!blsData.success) {
        return blsData;
      }

      // Process BLS data and update database
      const processedData = this.processBlsData(blsData.data);
      
      return {
        success: true,
        data: processedData,
        message: 'Occupation data updated from BLS successfully'
      };
    } catch (error) {
      console.error(`Error updating occupation from BLS: ${occupationCode}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Process BLS data into our database format
   * @param {Object} blsData - Raw BLS data
   */
  processBlsData(blsData) {
    // Extract relevant data from BLS response
    const employment = blsData.employment?.data?.Results?.series?.[0]?.data || [];
    const wages = blsData.wages?.data?.Results?.series?.[0]?.data || [];
    const projections = blsData.projections?.data?.Results?.series?.[0]?.data || [];

    // Process employment data
    const latestEmployment = employment[0];
    const employmentTrend = this.calculateTrend(employment);

    // Process wage data
    const latestWage = wages[0];
    const wageTrend = this.calculateTrend(wages);

    // Process projections
    const latestProjection = projections[0];
    const growthRate = this.calculateGrowthRate(projections);

    return {
      employment: {
        current: latestEmployment?.value || null,
        trend: employmentTrend,
        data: employment
      },
      wages: {
        current: latestWage?.value || null,
        trend: wageTrend,
        data: wages
      },
      projections: {
        current: latestProjection?.value || null,
        growthRate: growthRate,
        data: projections
      },
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Calculate trend from data series
   * @param {Array} data - Array of data points
   */
  calculateTrend(data) {
    if (data.length < 2) return 'stable';
    
    const recent = data[0]?.value;
    const previous = data[1]?.value;
    
    if (!recent || !previous) return 'unknown';
    
    const change = ((recent - previous) / previous) * 100;
    
    if (change > 5) return 'growing';
    if (change < -5) return 'declining';
    return 'stable';
  }

  /**
   * Calculate growth rate from projections
   * @param {Array} projections - Array of projection data
   */
  calculateGrowthRate(projections) {
    if (projections.length < 2) return null;
    
    const start = projections[projections.length - 1]?.value;
    const end = projections[0]?.value;
    
    if (!start || !end) return null;
    
    return ((end - start) / start) * 100;
  }
}

export default new BlsApiService();
